// Layer 3 (Transformations): the Training compliance tab's secondary currency
// read. Pure functions only: no React, no engine mutations, no side effects.

import { TRAINING_MODULES, type TrainingDoc } from './seed';

// ---------------------------------------------------------------------------
// Currency table — one row per training module
// ---------------------------------------------------------------------------

export type CurrencyStatus = 'current' | 'refresher-due' | 'revision-pending';

export const CURRENCY_LABEL: Record<CurrencyStatus, string> = {
  current: 'Current',
  'refresher-due': 'Refresher due',
  'revision-pending': 'Revision pending',
};

export function currencyTone(status: CurrencyStatus): 'success' | 'warning' | 'danger' {
  if (status === 'revision-pending') return 'warning';
  if (status === 'refresher-due') return 'danger';
  return 'success';
}

/**
 * Precedence: a drafted-but-unpublished revision outranks a stale trainee — the
 * module is about to change under whoever is trained on it. Otherwise any
 * target trained on a version other than the current one is due a refresher.
 */
export function moduleCurrency(doc: TrainingDoc): CurrencyStatus {
  if (doc.pendingRevision) return 'revision-pending';
  if (doc.refresherTargets.some((target) => target.trainedOnVersion !== doc.version)) {
    return 'refresher-due';
  }
  return 'current';
}

/** `trainedOn` is the module's refresher targets as one line, empty when it has none. */
export function trainedOnLine(doc: TrainingDoc): string {
  return doc.refresherTargets
    .map((target) => `${target.personName} · trained on ${target.trainedOnVersion}`)
    .join(' · ');
}

export type TrainingCurrencyRow = {
  id: string;
  title: string;
  audienceRole: string;
  version: string;
  /** '' when no trainee is on record — the row then renders no second line. */
  trainedOn: string;
  status: CurrencyStatus;
  tone: 'success' | 'warning' | 'danger';
};

export function trainingCurrencyRows(): TrainingCurrencyRow[] {
  return TRAINING_MODULES.map((doc) => {
    const status = moduleCurrency(doc);
    return {
      id: doc.id,
      title: doc.title,
      audienceRole: doc.audienceRole,
      version: doc.version,
      trainedOn: trainedOnLine(doc),
      status,
      tone: currencyTone(status),
    };
  });
}

// ---------------------------------------------------------------------------
// Currency meter — modules at their current version ÷ modules in the library
// ---------------------------------------------------------------------------

export type TrainingCurrencyMeter = {
  current: number;
  total: number;
  caption: string;
};

export function trainingCurrencyMeter(): TrainingCurrencyMeter {
  const rows = trainingCurrencyRows();
  const current = rows.filter((row) => row.status === 'current').length;
  return { current, total: rows.length, caption: `${current} of ${rows.length} current` };
}
